import { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  SkeletonBodyText,
  Modal,
  TextField,
  Divider,
  ChoiceList,
  Spinner,
} from '@shopify/polaris';
import {
  useGA4Config,
  useConfigureGA4,
  useToggleGA4,
  useTestGA4,
  useDisconnectGA4,
  useConnectGA4,
  useGA4Assets,
  useSelectGA4Asset,
  useCreateGA4Property,
} from '../hooks/api';
import { useLanguage, useTranslation } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { openOAuthWindow, navigateOAuth } from '../lib/auth';
import { useOAuthCallbackError } from '../hooks/useOAuthCallbackError';
import { GateBanner, type PageError } from '../components/GateBanner';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'connected':
      return <Badge tone="success">Connected</Badge>;
    case 'warning':
      return <Badge tone="warning">Warning</Badge>;
    case 'error':
      return <Badge tone="critical">Error</Badge>;
    case 'disabled':
      return <Badge>Disabled</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

export function GA4() {
  const { data: config, isLoading } = useGA4Config();
  const configureMutation = useConfigureGA4();
  const toggleMutation = useToggleGA4();
  const testMutation = useTestGA4();
  const disconnectMutation = useDisconnectGA4();
  const { language } = useLanguage();
  const t = useTranslation();

  const connectMutation = useConnectGA4();
  const selectAssetMutation = useSelectGA4Asset();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedStream, setSelectedStream] = useState<string>('');
  const [measurementId, setMeasurementId] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string } | null>(null);
  const [configError, setConfigError] = useState<PageError>(null);
  const [actionError, setActionError] = useState<PageError>(null);
  // Surface OAuth-callback failures delivered via ?error= (e.g. partial consent).
  useOAuthCallbackError(setActionError);
  const [connectError, setConnectError] = useState<PageError>(null);

  // After the OAuth round-trip, the callback redirects back with
  // ?step=select-property. Open the asset picker (works on both platforms).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthErr = params.get('error');
    if (oauthErr) {
      setConnectError(
        /access_denied|user_denied|cancell?ed/i.test(oauthErr)
          ? 'The Google Analytics connection was canceled. Nothing was changed - you can connect again anytime.'
          : `The Google Analytics connection could not be completed (${oauthErr.replace(/^[a-z0-9_]*?_/, '')}). Please try again.`
      );
    }
    if (params.get('step') === 'select-property' || params.get('ga4') === 'connected') {
      setShowPicker(true);
    }
  }, []);

  const assetsQuery = useGA4Assets(showPicker);
  const createPropertyMutation = useCreateGA4Property();
  const [selectedAccount, setSelectedAccount] = useState<string>('');


  const handleConnect = useCallback(async () => {
    setConnectError(null);
    const win = openOAuthWindow();
    try {
      const { url } = await connectMutation.mutateAsync();
      navigateOAuth(win, url);
    } catch (error: any) {
      win?.close();
      setConnectError(error?.message ? error : 'Could not start the Google connection.');
    }
  }, [connectMutation]);

  const closePicker = useCallback(() => {
    setShowPicker(false);
    setSelectedStream('');
    // Strip the OAuth return params from the URL.
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    params.delete('ga4');
    params.delete('platform');
    const clean = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', clean);
  }, []);

  // Turnkey for merchants with no GA4 property: create property + web stream +
  // MP secret in one explicit click (same analytics.edit scope as the picker).
  const handleCreateProperty = useCallback(async () => {
    const accounts = assetsQuery.data?.accounts ?? [];
    const accountName = selectedAccount || accounts[0]?.name;
    if (!accountName) return;
    try {
      await createPropertyMutation.mutateAsync({ accountName });
      closePicker();
    } catch (error: any) {
      setConnectError(error?.message ? error : 'Could not create the GA4 property.');
    }
  }, [assetsQuery.data, selectedAccount, createPropertyMutation, closePicker]);

  const handleSelectAsset = useCallback(async () => {
    const stream = assetsQuery.data?.streams.find((s) => s.dataStreamId === selectedStream);
    if (!stream) return;
    try {
      await selectAssetMutation.mutateAsync({
        dataStreamId: stream.dataStreamId,
        measurementId: stream.measurementId,
      });
      closePicker();
    } catch (error: any) {
      setConnectError(error?.message ? error : 'Could not finish the connection.');
    }
  }, [assetsQuery.data, selectedStream, selectAssetMutation, closePicker]);

  const handleConfigure = useCallback(async () => {
    setConfigError(null);
    if (!measurementId || !apiSecret) {
      setConfigError('Measurement ID and API Secret are required.');
      return;
    }

    try {
      await configureMutation.mutateAsync({ measurementId, apiSecret });
      setShowConfigModal(false);
      setMeasurementId('');
      setApiSecret('');
    } catch (error: any) {
      setConfigError(error?.message ? error : t.common.configFailed);
    }
  }, [measurementId, apiSecret, configureMutation]);

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      setActionError(null);
      try {
        await toggleMutation.mutateAsync(enabled);
      } catch (error: any) {
        console.error('Failed to toggle:', error);
        setActionError(error?.message ? error : t.common.actionFailed);
      }
    },
    [toggleMutation]
  );

  const handleTest = useCallback(async () => {
    setTestResult(null);
    setActionError(null);
    try {
      const result: any = await testMutation.mutateAsync();
      setTestResult({
        success: true,
        message: result.message || 'Test event sent successfully.',
      });
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Test event failed.' });
    }
  }, [testMutation]);

  const handleDisconnect = useCallback(async () => {
    setActionError(null);
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
    } catch (error: any) {
      console.error('Failed to disconnect:', error);
      setActionError(error?.message ? error : t.common.actionFailed);
    }
  }, [disconnectMutation]);

  if (isLoading) {
    return (
      <Page title="Google Analytics 4">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Google Analytics 4"
      subtitle="Server-side tracking via Measurement Protocol"
      primaryAction={
        config?.connected
          ? {
              content: config.isEnabled ? 'Disable Tracking' : 'Enable Tracking',
              onAction: () => handleToggle(!config.isEnabled),
              loading: toggleMutation.isPending,
            }
          : undefined
      }
    >
      <Layout>
        {/* Action Error Banner */}
        {actionError && (
          <Layout.Section>
            <GateBanner error={actionError} onDismiss={() => setActionError(null)} />
          </Layout.Section>
        )}

        {/* Connect Error Banner */}
        {connectError && (
          <Layout.Section>
            <GateBanner error={connectError} onDismiss={() => setConnectError(null)} />
          </Layout.Section>
        )}

        {/* Error Banner */}
        {config?.lastErrorMessage && (
          <Layout.Section>
            <Banner tone="warning" title="Warning">
              <p>{config.lastErrorMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Test Result */}
        {testResult && (
          <Layout.Section>
            <Banner
              tone={testResult.success ? 'success' : 'critical'}
              onDismiss={() => setTestResult(null)}
            >
              <p>{testResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Connection Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Connection Status
                </Text>
                {config?.connected && <StatusBadge status={config.status || 'connected'} />}
              </InlineStack>

              {config?.connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200">
                    <Text as="span" variant="bodyMd" tone="subdued">
                      Measurement ID:
                    </Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {config.measurementId}
                    </Text>
                  </InlineStack>

                  {config.lastSyncAt && (
                    <InlineStack gap="200">
                      <Text as="span" variant="bodyMd" tone="subdued">
                        Last Sync:
                      </Text>
                      <Text as="span" variant="bodyMd">
                        {new Date(config.lastSyncAt).toLocaleString(localeMap[language])}
                      </Text>
                    </InlineStack>
                  )}

                  <Divider />

                  <InlineStack gap="200">
                    <Button onClick={handleTest} loading={testMutation.isPending}>
                      Send Test Event
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      Update Configuration
                    </Button>
                    <Button
                      variant="plain"
                      tone="critical"
                      onClick={() => setShowDisconnectModal(true)}
                    >
                      Disconnect
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Connect your Google Analytics 4 property in one click. We create the
                    Measurement Protocol secret for you automatically, so there is nothing to copy
                    and paste. Server-side tracking bypasses ad blockers and browser restrictions.
                  </Text>
                  <InlineStack gap="200">
                    <Button variant="primary" onClick={handleConnect} loading={connectMutation.isPending}>
                      Connect Google Analytics
                    </Button>
                    <Button onClick={() => setShowConfigModal(true)}>
                      Advanced: enter manually
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Setup Instructions */}
        {!config?.connected && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Setup Instructions
                </Text>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    1. Go to Google Analytics &gt; Admin &gt; Data Streams and select your web stream.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    2. Copy your Measurement ID (starts with G-).
                  </Text>
                  <Text as="p" variant="bodyMd">
                    3. Under "Measurement Protocol API secrets", create a new API secret.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    4. Copy the secret value and paste it along with your Measurement ID below.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    5. Click "Save Configuration" and send a test event to verify the connection.
                  </Text>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Configuration Modal */}
        <Modal
          open={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setConfigError(null);
          }}
          title="Configure Google Analytics 4"
          primaryAction={{
            content: 'Save Configuration',
            onAction: handleConfigure,
            loading: configureMutation.isPending,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => {
                setShowConfigModal(false);
                setConfigError(null);
              },
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              {configError && (
                <GateBanner error={configError} />
              )}

              <TextField
                label="Measurement ID"
                value={measurementId}
                onChange={setMeasurementId}
                placeholder="G-XXXXXXXXXX"
                autoComplete="off"
                helpText="Found in Google Analytics > Admin > Data Streams > your web stream."
              />

              <TextField
                label="API Secret"
                value={apiSecret}
                onChange={setApiSecret}
                type="password"
                placeholder="Enter your Measurement Protocol API secret"
                autoComplete="off"
                helpText="Create one in Data Streams > Measurement Protocol API secrets."
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Asset Picker Modal (after one-click Connect) */}
        <Modal
          open={showPicker}
          onClose={closePicker}
          title="Choose your GA4 property"
          primaryAction={{
            content: 'Finish setup',
            onAction: handleSelectAsset,
            loading: selectAssetMutation.isPending,
            disabled: !selectedStream,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: closePicker }]}
        >
          <Modal.Section>
            {assetsQuery.isLoading ? (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" accessibilityLabel="Loading properties" />
                <Text as="span" variant="bodyMd">Loading your Google Analytics properties...</Text>
              </InlineStack>
            ) : assetsQuery.isError ? (
              <Banner tone="critical">
                <p>Could not load your properties. The connection may have expired - click Connect again.</p>
              </Banner>
            ) : (assetsQuery.data?.streams.length ?? 0) === 0 ? (
              (assetsQuery.data?.accounts.length ?? 0) > 0 ? (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    No GA4 property found on this Google account. Trackful can create one for
                    your store - property, web data stream and Measurement Protocol secret -
                    in one click.
                  </Text>
                  {(assetsQuery.data?.accounts.length ?? 0) > 1 && (
                    <ChoiceList
                      title="Analytics account"
                      choices={(assetsQuery.data?.accounts ?? []).map((a) => ({
                        label: a.displayName,
                        value: a.name,
                      }))}
                      selected={selectedAccount ? [selectedAccount] : [assetsQuery.data?.accounts[0]?.name ?? '']}
                      onChange={(v) => setSelectedAccount(v[0] ?? '')}
                    />
                  )}
                  <InlineStack>
                    <Button
                      variant="primary"
                      onClick={handleCreateProperty}
                      loading={createPropertyMutation.isPending}
                    >
                      Create a GA4 property for my store
                    </Button>
                  </InlineStack>
                </BlockStack>
              ) : (
                <Banner tone="warning">
                  <p>
                    This Google account has no Google Analytics account yet. Create one at
                    analytics.google.com (30 seconds), then click Connect again - Trackful
                    handles everything else.
                  </p>
                </Banner>
              )
            ) : (
              <ChoiceList
                title="We will create the Measurement Protocol secret automatically for the stream you pick."
                choices={(assetsQuery.data?.streams ?? []).map((s) => ({
                  label: `${s.propertyName} - ${s.streamName} (${s.measurementId})`,
                  value: s.dataStreamId,
                }))}
                selected={selectedStream ? [selectedStream] : []}
                onChange={(v) => setSelectedStream(v[0] ?? '')}
              />
            )}
          </Modal.Section>
        </Modal>

        {/* Disconnect Confirmation Modal */}
        <Modal
          open={showDisconnectModal}
          onClose={() => setShowDisconnectModal(false)}
          title="Disconnect Google Analytics 4"
          primaryAction={{
            content: 'Disconnect',
            onAction: handleDisconnect,
            loading: disconnectMutation.isPending,
            destructive: true,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowDisconnectModal(false),
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              Are you sure you want to disconnect Google Analytics 4? Server-side tracking will stop
              immediately and conversion data will no longer be sent to GA4.
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
